import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import { CameraDetails, CameraImageTimelineResponse, Store } from '../../types';
import ModList from './components/ModList';
import { SelectedCameraView } from './components/SelectedCameraView';

export const StoreDetailsPage: React.FC = () => {
  const { storeName, deviceName } = useParams<{
    storeName: string;
    deviceName?: string;
  }>();
  const [searchParams] = useSearchParams();
  const imageId = searchParams.get('image_id');
  const navigate = useNavigate();
  const { token, logout } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();
  const [store, setStore] = useState<Store | null>(null);
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<CameraDetails | null>(null);
  const [cameraTimeline, setCameraTimeline] = useState<CameraImageTimelineResponse | null>(null);
  const [isModListCollapsed, setIsModListCollapsed] = useState(() => {
    const wasCollapsed = localStorage.getItem('isModListCollapsed');
    return deviceName && wasCollapsed === 'true';
  });

  // Persist collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('isModListCollapsed', String(isModListCollapsed));
  }, [isModListCollapsed]);

  // Load store and cameras data
  useEffect(() => {
    if (!storeName) return;

    const loadStoreData = async () => {
      toggleLoading(true);
      try {
        const stores = await ApiService.fetchStores(token);
        const currentStore = stores.find((s) => s.storeName === storeName);

        if (!currentStore) {
          showErrorToast('Store not found');
          navigate('/stores');
          return;
        }

        setStore(currentStore);
        const cameras = await ApiService.fetchCameras(token, storeName);
        setCameras(cameras);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Error loading store data');
      } finally {
        toggleLoading(false);
      }
    };

    loadStoreData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, token]);

  // Handle device selection from URL
  useEffect(() => {
    if (!deviceName || cameras.length === 0) return;

    const targetCamera = cameras.find((camera) => camera.deviceName === deviceName);
    if (targetCamera) {
      setSelectedCamera(targetCamera);
    } else {
      showErrorToast('Device not found');
      navigate(`/stores/${storeName}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceName, cameras]);

  // Handle device selection from URL
  useEffect(() => {
    if (imageId && storeName) {
      ApiService.fetchCameraImage(token, imageId)
        .then(({ image, templates, pogs }) => {
          setCameraTimeline({ images: [image], templates, pogs });
        })
        .catch(() => {
          showErrorToast('Failed to fetch camera image');
          navigate(`/stores/${storeName}`);
        });

      return;
    }

    if (!selectedCamera || !storeName) return;

    // Fetch camera timeline for the selected device
    ApiService.fetchCameraTimeline(token, storeName!, selectedCamera?.deviceName)
      .then((timeline) => {
        setCameraTimeline(timeline);
      })
      .catch((timelineErr) => {
        console.warn('Failed to fetch camera timeline:', timelineErr);
        showErrorToast('Failed to fetch camera timeline');
        navigate(`/stores/${storeName}`);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCamera, imageId]);

  const handleCameraSelect = (camera: CameraDetails) => {
    // Update URL to include device name - this will trigger the useEffect to handle camera selection
    if (storeName) {
      navigate(`/stores/${storeName}/${camera.deviceName}`, { replace: true });
    }
  };

  const handleChangeStore = () => {
    navigate('/stores');
  };

  if (!store) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="w-full mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">{store.legibleName}</h1>
              <p className="text-gray-600 text-sm">{store.storeName}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleChangeStore}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Change Store
              </button>
              <Link
                to={`/stores/${store.storeName}/mod-eligibility`}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Manage PC &amp; PR
              </Link>
              <Link
                to={`/stores/${store.storeName}/pog-reset`}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                POG Reset
              </Link>
              <Link
                to={`/tasks?chain_id=${store.chainId}&is_active=true&is_audited=false&task_date=${
                  new Date().toISOString().split('T')[0]
                }`}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Tasks
              </Link>
              <Link
                to={`/embeddings?chain_id=${store.chainId}&store_id=${store.storeId}`}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Embeddings
              </Link>
              <button
                onClick={() => logout()}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto p-4">
        <div className="flex gap-4">
          {/* Collapsible ModList Sidebar */}
          <div className={`transition-all duration-300 ${isModListCollapsed ? 'w-12' : 'w-80'}`}>
            {isModListCollapsed ? (
              <div className="bg-white rounded-lg shadow-md p-3">
                <button
                  onClick={() => setIsModListCollapsed(false)}
                  className="w-full flex items-center justify-center text-gray-600 hover:text-gray-900"
                  title="Expand camera list"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setIsModListCollapsed(true)}
                  className="absolute top-6 -right-3 z-10 bg-white rounded-full p-1 shadow-sm border text-gray-600 hover:text-gray-900"
                  title="Collapse camera list"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <ModList
                  cameras={cameras}
                  selectedCamera={selectedCamera}
                  onCameraSelect={handleCameraSelect}
                  storeName={storeName}
                  storeTimeZone={store.timeZone}
                />
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {selectedCamera ? (
              <SelectedCameraView
                key={selectedCamera.deviceName}
                camera={selectedCamera}
                storeName={store.storeName}
                cameraTimeline={cameraTimeline}
                chainId={store.chainId}
                storeId={store.storeId}
                storeTimeZone={store.timeZone}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-gray-500 text-center">
                  {isModListCollapsed
                    ? 'Expand the sidebar to select a camera location'
                    : 'Select a location from the list on the left'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
